/**
 * Health — the reference example of the shared-schema pattern (Phase 1 step 4).
 *
 * Two routes, deliberately different in what they cost and what they require:
 *
 *  GET /api/health       anonymous, shallow, NO database access. It is the
 *                        Lambda Web Adapter readiness probe and the SPA's
 *                        stale-build poll (§3d), so it must stay cheap and it
 *                        must never depend on RDS being reachable.
 *
 *  GET /api/health/deep  requires a token, does a real `SELECT 1`, and is
 *                        exempt from Phase 3's export-policy middleware so a
 *                        monitoring identity gets a health answer rather than
 *                        403 EXPORT_POLICY_NOT_ACKNOWLEDGED.
 */

import { z } from 'zod';
import { dataEnvelope } from './http.js';

export const healthSchema = z.object({
  status: z.literal('ok'),
  /**
   * Identifies the deployed artifact. The SPA polls this and compares it to
   * the id it loaded with: because the SPA is bundled INTO the Lambda package,
   * the previous build's hashed chunks cease to exist the instant the function
   * updates (§3d), and this is how the client finds out.
   */
  buildId: z.string().min(1),
});

export type Health = z.infer<typeof healthSchema>;
export const healthResponseSchema = dataEnvelope(healthSchema);
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const deepHealthSchema = healthSchema.extend({
  db: z.literal('ok'),
  version: z.string().min(1),
});

export type DeepHealth = z.infer<typeof deepHealthSchema>;
export const deepHealthResponseSchema = dataEnvelope(deepHealthSchema);
export type DeepHealthResponse = z.infer<typeof deepHealthResponseSchema>;
